import { Injectable, signal, WritableSignal } from '@angular/core';
import { SESService } from './ses.service';
import { environment } from '../../environments/environment';

/**
 * EmailService - Handles AWS SES email template operations
 *
 * NOTE: Firestore email data is now managed by FirestoreStoreService.
 * This service only handles AWS SES template operations.
 */
@Injectable({
  providedIn: 'root',
})
export class EmailService {
  // AWS SES email templates
  emailTemplates: WritableSignal<
    { Name: string; CreatedTimestamp: Date | null }[]
  > = signal([]);

  // Loading state for AWS SES templates
  templatesLoading: WritableSignal<boolean> = signal(true);

  // Environment-based prefix filter
  private isDev = !environment.production;

  constructor(private sesService: SESService) {}

  /**
   * Initialize by fetching AWS SES email templates
   */
  async init() {
    await this.refreshEmailTemplates();
  }

  /**
   * Refresh email templates from AWS SES
   */
  async refreshEmailTemplates() {
    this.templatesLoading.set(true);
    try {
      const allTemplates = await this.sesService.getAllEmailTemplates();
      // Filter templates based on environment
      const filteredTemplates = this.filterByEnvironment(allTemplates, 'Name');
      // Parse CreatedTimestamp strings to Date objects
      const parsedTemplates = filteredTemplates.map((t) => ({
        Name: t.Name,
        CreatedTimestamp: t.CreatedTimestamp
          ? new Date(t.CreatedTimestamp)
          : null,
      }));
      this.emailTemplates.set(parsedTemplates);
    } finally {
      this.templatesLoading.set(false);
    }
  }

  /**
   * Filter items based on environment (dev prefix filter)
   * In DEV: only show items with 'dev' prefix
   * In PROD: only show items WITHOUT 'dev' prefix
   */
  private filterByEnvironment<T>(items: T[], nameField: keyof T): T[] {
    return items.filter((item) => {
      const name = ((item[nameField] as string) || '').toLowerCase();
      const hasDevPrefix = name.startsWith('dev-') || name.startsWith('dev_');
      return this.isDev ? hasDevPrefix : !hasDevPrefix;
    });
  }
}
